import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  LinearProgress,
  Avatar,
  Pagination,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  LocationCity as LocationCityIcon,
  Router as RouterIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';

import { caixaFinanceiroService, CaixaFinanceiro, CaixaFilters } from '../services/caixaFinanceiroService';
import CaixaFinanceiroForm from '../components/forms/CaixaFinanceiroForm';
import CaixaFinanceiroDashboard from './CaixaFinanceiroDashboard';
import { formatarValor } from '../utils/formatToView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`caixa-financeiro-tabpanel-${index}`}
      aria-labelledby={`caixa-financeiro-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `caixa-financeiro-tab-${index}`,
    'aria-controls': `caixa-financeiro-tabpanel-${index}`,
  };
}

const CaixaFinanceiroPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const primario = theme.palette.primary.main;

  // Estados das abas
  const [tabValue, setTabValue] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === 'gerenciar' ? 1 : 0;
  });

  // Estados principais do gerenciamento
  const [caixas, setCaixas] = useState<CaixaFinanceiro[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados de paginação
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Estados de filtros
  const [filters, setFilters] = useState<CaixaFilters>({
    page: 1,
    limit: 10,
    status: 'todos',
    tipo_vinculo: 'todos',
    ativo: 'true'
  });

  // Estados do modal de criação/edição
  const [formModal, setFormModal] = useState({
    open: false,
    caixa: null as CaixaFinanceiro | null
  });

  // Estados do modal de fechamento
  const [fecharModal, setFecharModal] = useState({
    open: false,
    caixa: null as CaixaFinanceiro | null
  });

  // Estados de busca
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Carregar dados apenas se estiver na aba de gerenciamento
    if (tabValue === 1) {
      loadCaixas();
    }
  }, [filters, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Atualizar URL para manter o estado da aba
    if (newValue === 0) {
      setSearchParams({});
    } else {
      setSearchParams({ tab: 'gerenciar' });
    }
  };

  const loadCaixas = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await caixaFinanceiroService.getCaixas(filters);

      if (response.success) {
        setCaixas(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar caixas');
      }
    } catch (err: any) {
      console.error('Erro ao carregar caixas:', err);
      setError(err.message || 'Erro ao carregar dados dos caixas');
      setCaixas([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (field: keyof CaixaFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset para primeira página
    }));
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setFilters(prev => ({
        ...prev,
        search: searchTerm.trim(),
        page: 1
      }));
    } else {
      const { search, ...filtersWithoutSearch } = filters;
      setFilters({ ...filtersWithoutSearch, page: 1 });
    }
  };

  const handleOpenForm = (caixa: CaixaFinanceiro | null = null) => {
    setFormModal({ open: true, caixa });
  };

  const handleCloseForm = () => {
    setFormModal({ open: false, caixa: null });
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    loadCaixas();
  };

  const handleViewDetails = (caixa: CaixaFinanceiro) => {
    navigate(`/caixa-financeiro/${caixa.id}`);
  };

  const handleFecharCaixa = async (caixa: CaixaFinanceiro) => {
    if (caixa.is_default) {
      setError('Caixa default não pode ser fechado');
      return;
    }
    setFecharModal({ open: true, caixa });
  };

  const confirmFecharCaixa = async () => {
    if (!fecharModal.caixa) return;

    try {
      const response = await caixaFinanceiroService.fecharCaixa(fecharModal.caixa.id);
      if (response.success) {
        setFecharModal({ open: false, caixa: null });
        loadCaixas();
      } else {
        throw new Error(response.message || 'Erro ao fechar caixa');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao fechar caixa');
    }
  };

  const handleToggleStatus = async (caixa: CaixaFinanceiro) => {
    if (caixa.is_default && caixa.ativo) {
      setError('Caixa default não pode ser desativado');
      return;
    }

    try {
      const response = await caixaFinanceiroService.toggleCaixaStatus(caixa.id);
      if (response.success) {
        loadCaixas();
      } else {
        throw new Error(response.message || 'Erro ao alterar status');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar status do caixa');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'success';
      case 'fechado':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'cidade':
        return <LocationCityIcon fontSize="small" />;
      case 'usuario':
        return <PersonIcon fontSize="small" />;
      case 'gateway':
        return <RouterIcon fontSize="small" />;
      case 'default':
        return <SettingsIcon fontSize="small" />;
      default:
        return <SettingsIcon fontSize="small" />;
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'cidade':
        return 'Cidade';
      case 'usuario':
        return 'Usuário';
      case 'gateway':
        return 'Gateway';
      case 'default':
        return 'Default';
      default:
        return tipo;
    }
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
      {/* Cabeçalho */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <AccountBalanceIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Caixa Financeiro
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
              Dashboard e gerenciamento de caixas financeiros
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={0.5}>
            {tabValue === 1 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={loadCaixas}
                  disabled={loading}
                >
                  Atualizar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => handleOpenForm()}
                >
                  Novo Caixa
                </Button>
              </>
            )}
          </Box>
        </Box>
        <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"            
            sx={{
              px: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
            }}
          >
            <Tab
              icon={<DashboardIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Dashboard"
              {...a11yProps(0)}
            />
            <Tab
              icon={<ManageAccountsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Gerenciar Caixas"
              {...a11yProps(1)}
            />
          </Tabs>
        </Paper>
     

      {/* Tabs */}
      <Card sx={{ mb: 1 }}>

        {/* Tab Panel 1 - Dashboard */}
        <TabPanel value={tabValue} index={0}>
          <Box p={1}>
            <CaixaFinanceiroDashboard />
          </Box>
        </TabPanel>

        {/* Tab Panel 2 - Gerenciar Caixas */}
        <TabPanel value={tabValue} index={1}>
          <Box p={1}>
            {/* Filtros */}
            <Card sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                  Filtros
                </Typography>

                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Buscar por nome"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      sx={{ '& .MuiInputBase-root': { height: 32 } }}
                      InputProps={{
                        endAdornment: (
                          <IconButton onClick={handleSearch} size="small" sx={{ p: 0.25 }}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
                      <Select
                        value={filters.status || 'todos'}
                        label="Status"
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="aberto">Aberto</MenuItem>
                        <MenuItem value="fechado">Fechado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Tipo</InputLabel>
                      <Select
                        value={filters.tipo_vinculo || 'todos'}
                        label="Tipo"
                        onChange={(e) => handleFilterChange('tipo_vinculo', e.target.value)}
                        sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="cidade">Cidade</MenuItem>
                        <MenuItem value="usuario">Usuário</MenuItem>
                        <MenuItem value="gateway">Gateway</MenuItem>
                        <MenuItem value="default">Default</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Situação</InputLabel>
                      <Select
                        value={filters.ativo || 'true'}
                        label="Situação"
                        onChange={(e) => handleFilterChange('ativo', e.target.value)}
                        sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                      >
                        <MenuItem value="true">Ativo</MenuItem>
                        <MenuItem value="false">Inativo</MenuItem>
                        <MenuItem value="todos">Todos</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Alerta de erro */}
            {error && (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }} onClose={() => setError(null)}>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            )}

            {/* Progress bar */}
            {loading && <LinearProgress sx={{ mb: 1 }} />}

            {/* Tabela */}
            <Card>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Caixas Cadastrados ({pagination.total})
                  </Typography>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{
                        '& .MuiTableCell-head': {
                          bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                          fontWeight: 700,
                          fontSize: '0.73rem',
                          py: 0.8,
                          borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                        },
                      }}>
                        <TableCell>Nome</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Vinculação</TableCell>
                        <TableCell align="right">Saldo Atual</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Situação</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                              Carregando caixas...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : caixas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                              Nenhum caixa encontrado
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        caixas.map((caixa) => (
                          <TableRow key={caixa.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {caixa.is_default && (
                                  <Chip
                                    label="DEFAULT"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 16, fontSize: '0.625rem' }}
                                  />
                                )}
                                <Typography fontWeight="medium" variant="body2">
                                  {caixa.nome}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {getTipoIcon(caixa.tipo_vinculo)}
                                <Typography variant="caption">
                                  {getTipoLabel(caixa.tipo_vinculo)}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Typography variant="caption">
                                {caixa.escritorio?.identificacao ||
                                  caixa.usuario?.nome ||
                                  'Sistema'}
                              </Typography>
                            </TableCell>

                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.25}>
                                {caixa.saldo_atual >= 0 ? (
                                  <TrendingUpIcon color="success" sx={{ fontSize: 14 }} />
                                ) : (
                                  <TrendingDownIcon color="error" sx={{ fontSize: 14 }} />
                                )}
                                <Typography
                                  fontWeight="bold"
                                  color={caixa.saldo_atual >= 0 ? 'success.main' : 'error.main'}
                                  variant="body2"
                                >
                                  {formatarValor(caixa.saldo_atual)}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Chip
                                label={caixa.status === 'aberto' ? 'Aberto' : 'Fechado'}
                                color={getStatusColor(caixa.status) as any}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.625rem' }}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Chip
                                label={caixa.ativo ? 'Ativo' : 'Inativo'}
                                color={caixa.ativo ? 'success' : 'error'}
                                size="small"
                                variant="filled"
                                sx={{ height: 20, fontSize: '0.625rem' }}
                              />
                            </TableCell>

                            <TableCell align="center" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                              <Box display="flex" gap={0.25} justifyContent="center">
                                <Tooltip title="Ver detalhes">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDetails(caixa)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <VisibilityIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenForm(caixa)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>

                                {caixa.status === 'aberto' && !caixa.is_default && (
                                  <Tooltip title="Fechar caixa">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => handleFecharCaixa(caixa)}
                                      sx={{ p: 0.25 }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {!caixa.is_default && (
                                  <Tooltip title={caixa.ativo ? 'Desativar' : 'Ativar'}>
                                    <IconButton
                                      size="small"
                                      color={caixa.ativo ? 'error' : 'success'}
                                      onClick={() => handleToggleStatus(caixa)}
                                      sx={{ p: 0.25 }}
                                    >
                                      {caixa.ativo ? (
                                        <LockIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <LockOpenIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paginação */}
                {pagination.pages > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 1.5,
                      px: 2,
                      mt: 1,
                      bgcolor: alpha(primario, 0.04),
                      borderTop: `2px solid ${alpha(primario, 0.2)}`,
                    }}
                  >
                    <Pagination
                      count={pagination.pages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </TabPanel>
      </Card>

      {/* Modal de Formulário */}
      <Dialog
        open={formModal.open}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formModal.caixa ? 'Editar Caixa' : 'Novo Caixa'}
        </DialogTitle>
        <DialogContent>
          <CaixaFinanceiroForm
            caixa={formModal.caixa}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Fechamento */}
      <Dialog
        open={fecharModal.open}
        onClose={() => setFecharModal({ open: false, caixa: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Fechamento</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja fechar o caixa "{fecharModal.caixa?.nome}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita. O caixa não poderá mais receber
            novas movimentações.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFecharModal({ open: false, caixa: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmFecharCaixa}
          >
            Confirmar Fechamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaixaFinanceiroPage;